/* ============================================================================
   PLTXX PDP gallery — filter + re-sort controller (Path B: single authority)

   The native ProductCarousel variant->featured_media auto-slide is neutralized
   in markup (its hook class js-product-gallery-slide-variant-<id> was renamed to
   js-pltxx-slide-variant), so this is the ONLY thing that reacts to a variant
   change for the gallery.

   Server-side render already emits the correct order for the initial combo, so
   this controller does NOTHING until the (set, colour) combo actually changes.
   On change it: filters (hides wrong colour + wrong-colour packages), re-sorts
   (active package first, then rest in admin order, then the other 3 active-colour
   packages in tier order), and mirrors that into the thumbnail strip AND the zoom
   slider. Hidden media are parked (kept in the document, moved out of the swiper
   wrapper) so there are no empty slots and zoom/lightbox stay intact.

   Sort spec (per active tier + colour):
     1. active package  [paket:<tier>:<colour>]
     2. all other visible media in original admin order ([all], no-prefix, and
        the active-colour [<colour>] images) — packages excluded
     3. the other 3 active-colour packages, tier order essential->pro->elite->ultimate
   ========================================================================== */
(function () {
	"use strict";

	var TIER_SEQ = ["essential", "pro", "elite", "ultimate"];

	function norm(v) {
		return (v == null ? "" : String(v)).toLowerCase().trim().replace(/[\s-]?set$/, "").trim();
	}
	function normColor(v) {
		return (v == null ? "" : String(v)).toLowerCase().trim();
	}

	// Visibility for a media item given the active colour.
	function isVisible(m, color) {
		if (m.kind === "paket") return normColor(m.color) === color;
		if (m.kind === "color") return normColor(m.color) === color;
		return true; // all / plain
	}

	// Ordered list of visible media ids for a (tier, colour) combo.
	function computeOrder(media, tier, color) {
		var front = [];
		var mid = [];
		var buckets = {};
		var i, m;
		for (i = 0; i < media.length; i++) {
			m = media[i];
			if (!isVisible(m, color)) continue;
			if (m.kind === "paket") {
				if (norm(m.tier) === tier) front.push(m.id);
				else buckets[norm(m.tier)] = m.id;
			} else {
				mid.push(m.id);
			}
		}
		var tail = [];
		for (i = 0; i < TIER_SEQ.length; i++) {
			var t = TIER_SEQ[i];
			if (t !== tier && buckets[t]) tail.push(buckets[t]);
		}
		return front.concat(mid, tail);
	}

	// Build id -> node map for a set of nodes (by data-pltxx-media-id).
	function mapNodes(nodes) {
		var map = {};
		for (var i = 0; i < nodes.length; i++) {
			var id = nodes[i].getAttribute("data-pltxx-media-id");
			if (id) map[id] = nodes[i];
		}
		return map;
	}

	// Reorder one swiper region: visible ids into wrapper (in order), the rest into park.
	function reorderRegion(wrapper, park, nodesById, orderIds, allIds, renumber) {
		if (!wrapper || !park) return;
		var visibleSet = {};
		var i, id, node;
		for (i = 0; i < orderIds.length; i++) visibleSet[orderIds[i]] = true;

		// Park everything not visible (node stays in the document, out of the wrapper).
		for (i = 0; i < allIds.length; i++) {
			id = allIds[i];
			if (!visibleSet[id]) {
				node = nodesById[id];
				if (node && node.parentNode !== park) park.appendChild(node);
			}
		}
		// Place visible nodes into the wrapper in order (appendChild moves existing nodes).
		for (i = 0; i < orderIds.length; i++) {
			node = nodesById[orderIds[i]];
			if (node) wrapper.appendChild(node);
		}
		// Keep keyboard slide-index in sync with the new order.
		if (renumber) {
			for (i = 0; i < orderIds.length; i++) {
				node = nodesById[orderIds[i]];
				if (node) node.setAttribute("data-slide-index", String(i));
			}
		}
	}

	function safeUpdate(swiper) {
		if (!swiper) return;
		try {
			swiper.update();
			if (swiper.thumbs && typeof swiper.thumbs.update === "function") swiper.thumbs.update(true);
		} catch (e) {}
	}

	function setupGallery(container) {
		// Guard: never wire the same container twice (product-media can be
		// included more than once per page, and init() may run more than once).
		if (container.getAttribute("data-pltxx-ready")) return;
		var sectionId = container.getAttribute("data-pltxx-section-id");
		var cfg = (window.pltxxGallery && window.pltxxGallery[sectionId]) || null;
		if (!cfg || !cfg.media || !cfg.media.length) return;
		container.setAttribute("data-pltxx-ready", "1");

		// Scope option-radio lookups to THIS gallery's section so multiple
		// product galleries on one page (e.g. PDP + a featured-product section)
		// never read each other's Set/Farbe selection.
		var scope = container.closest("[data-section-id]") || document;

		var media = cfg.media;
		var allIds = media.map(function (m) { return m.id; });

		var sliderWrapper = container.querySelector("[data-pltxx-slider-wrapper]");
		var parkedSlides = container.querySelector("[data-pltxx-parked-slides]");
		var thumbsWrapper = container.querySelector("[data-pltxx-thumbs-wrapper]");
		var parkedThumbs = container.querySelector("[data-pltxx-parked-thumbs]");
		var zoomWrapper = container.querySelector("[data-pltxx-zoom-wrapper]");
		var parkedZoom = container.querySelector("[data-pltxx-parked-zoom]");

		var sliderEl = container.querySelector(".js-product-media-slider-" + sectionId);
		var thumbsEl = container.querySelector(".js-product-media-slider-thumbnails-" + sectionId);
		var zoomEl = container.querySelector("[data-zoom-slider]");

		// Node maps must include BOTH wrapper + parked children (all media).
		function slideNodes() {
			return mapNodes([].slice.call(container.querySelectorAll(".js-pltxx-gallery-slide")));
		}
		function thumbNodes() {
			return mapNodes([].slice.call(container.querySelectorAll(".js-pltxx-thumb")));
		}
		function zoomNodes() {
			return zoomWrapper ? mapNodes(
				[].slice.call(container.querySelectorAll("[data-pltxx-parked-zoom] > [data-pltxx-media-id], [data-pltxx-zoom-wrapper] > [data-pltxx-media-id]"))
			) : {};
		}

		var applied = { tier: norm(cfg.initTier), color: normColor(cfg.initColor) };

		function apply(tier, color) {
			tier = norm(tier);
			color = normColor(color);
			if (!tier || !color) return;
			if (tier === applied.tier && color === applied.color) return; // no-op: nothing changed

			var order = computeOrder(media, tier, color);
			if (!order.length) return; // safety: never blank the gallery

			// Main slider
			reorderRegion(sliderWrapper, parkedSlides, slideNodes(), order, allIds, false);
			// Thumbnails (mirror same order, renumber keyboard indices)
			reorderRegion(thumbsWrapper, parkedThumbs, thumbNodes(), order, allIds, true);
			// Zoom slider (same order; zoom-open maps by id → index parity)
			if (zoomWrapper && parkedZoom) reorderRegion(zoomWrapper, parkedZoom, zoomNodes(), order, allIds, false);

			var mainSwiper = sliderEl && sliderEl.swiper;
			var thumbSwiper = thumbsEl && thumbsEl.swiper;
			var zoomSwiper = zoomEl && zoomEl.swiper;

			safeUpdate(thumbSwiper);
			safeUpdate(mainSwiper);
			safeUpdate(zoomSwiper);

			// Reset to the active package (index 0) without animation, before any jump.
			try { if (mainSwiper) mainSwiper.slideTo(0, 0); } catch (e) {}
			try { if (thumbSwiper) thumbSwiper.slideTo(0, 0); } catch (e) {}

			applied = { tier: tier, color: color };
		}

		function applyFromRadios() {
			var o1 = scope.querySelector('[name="option1"]:checked');
			var o2 = scope.querySelector('[name="option2"]:checked');
			// Products without a colour option (no option2) never re-sort here;
			// the server-rendered order stands. No crash, no empty gallery.
			if (o1 && o2) apply(o1.value, o2.value);
		}

		// PRIMARY trigger: delegated change on the native Set/Farbe radios.
		// Race-free (no dependency on themeCore load order) and survives Jade
		// re-rendering the options markup. Scoped to this section's radios.
		document.addEventListener("change", function (e) {
			var t = e.target;
			if (t && (t.name === "option1" || t.name === "option2") && scope.contains(t)) applyFromRadios();
		}, true);

		// SECONDARY trigger: theme EventBus (retry until themeCore is ready).
		function onChangeVariant(payload) {
			if (payload && payload.variant) {
				var v = payload.variant;
				var tier = (v.options && v.options.length >= 2) ? v.options[0] : v.option1;
				var color = (v.options && v.options.length >= 2) ? v.options[1] : v.option2;
				apply(tier, color);
			} else {
				applyFromRadios();
			}
		}
		var busTries = 0;
		(function attachBus() {
			if (window.themeCore && window.themeCore.EventBus && typeof window.themeCore.EventBus.listen === "function") {
				window.themeCore.EventBus.listen("pdp:section-" + sectionId + ":change-variant", onChangeVariant);
				return;
			}
			if (busTries++ < 100) setTimeout(attachBus, 50);
		})();
	}

	function init() {
		var galleries = document.querySelectorAll("[data-pltxx-gallery]");
		for (var i = 0; i < galleries.length; i++) setupGallery(galleries[i]);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
